/**
 * La disposizione della dashboard: quali widget, in che ordine, di che taglia.
 *
 * Si salva cosi' nel database (tabella dashboard_layouts):
 *   { "versione": 1, "widget": [{ "id", "tipo", "taglia", "impostazioni" }] }
 *
 * La lettura non si fida mai di quello che trova: un widget ritirato, un
 * ruolo cambiato, un salvataggio di una versione piu' nuova dell'app non
 * devono rompere la pagina ne' far perdere quello che una persona si era
 * sistemata. Tutte le modifiche restituiscono una disposizione nuova, senza
 * toccare quella di partenza.
 */

import type { UserRole } from '../../types/roles';
import {
  CATALOGO,
  eTipoWidget,
  ruoloVede,
  type MappaImpostazioni,
  type Taglia,
  type TipoWidget,
} from './catalogo';

export const VERSIONE_LAYOUT = 1;
export const MAX_WIDGET = 24;
/** Sotto il limite del database (32 KB), per dare un errore chiaro prima. */
export const MAX_CARATTERI_LAYOUT = 30_000;

export interface WidgetIstanza<T extends TipoWidget = TipoWidget> {
  id: string;
  tipo: T;
  taglia: Taglia;
  impostazioni: MappaImpostazioni[T];
}

export interface LayoutDashboard {
  versione: typeof VERSIONE_LAYOUT;
  widget: WidgetIstanza[];
}

/**
 * - nessuno: la persona non ha mai personalizzato;
 * - ok: letto cosi' com'era;
 * - corretto: letto, ma qualcosa e' stato sistemato o scartato;
 * - illeggibile: non e' una disposizione, si mostra la base del ruolo;
 * - versione-futura: salvato da una versione piu' nuova dell'app, che questa
 *   non sa leggere: si mostra la base e non si lascia modificare, per non
 *   sovrascriverlo.
 */
export type EsitoLettura = 'nessuno' | 'ok' | 'corretto' | 'illeggibile' | 'versione-futura';

export interface LetturaLayout {
  esito: EsitoLettura;
  layout: LayoutDashboard;
  /**
   * Widget salvati che ora non si mostrano: tipo sconosciuto a questa versione
   * dell'app, o non permesso al ruolo di adesso. Si riscrivono uguali al
   * salvataggio: se il ruolo torna quello di prima, ricompaiono.
   */
  conservati: Record<string, unknown>[];
}

const ID_VALIDO = /^[A-Za-z0-9_-]{1,64}$/;

export function nuovoId(): string {
  return Math.random().toString(36).slice(2, 10);
}

const oggetto = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** La disposizione vuota: serve come segnaposto, mai come "base". */
export const LAYOUT_VUOTO: LayoutDashboard = { versione: VERSIONE_LAYOUT, widget: [] };

/** Una voce salvata (di un tipo gia' controllato) diventa un widget valido. */
function leggiVoce(
  voce: Record<string, unknown>,
  idUsati: Set<string>,
  generaId: () => string,
): { widget: WidgetIstanza; corretto: boolean } {
  const tipo = voce.tipo as TipoWidget;
  const def = CATALOGO[tipo];
  let corretto = false;

  let id = typeof voce.id === 'string' && ID_VALIDO.test(voce.id) ? voce.id : '';
  if (!id || idUsati.has(id)) {
    id = generaId();
    while (idUsati.has(id)) id = generaId();
    corretto = true;
  }
  idUsati.add(id);

  const tagliaOk = def.taglie.includes(voce.taglia as Taglia);
  if (!tagliaOk) corretto = true;

  const impostazioni = def.leggiImpostazioni(voce.impostazioni);
  if (JSON.stringify(impostazioni) !== JSON.stringify(voce.impostazioni ?? {})) corretto = true;

  return {
    widget: { id, tipo, taglia: tagliaOk ? (voce.taglia as Taglia) : def.tagliaPredefinita, impostazioni },
    corretto,
  };
}

/**
 * Legge la disposizione salvata. `base` e' la dashboard di partenza del
 * ruolo, usata quando non c'e' niente di leggibile.
 */
export function leggiLayout(
  grezzo: unknown,
  ruolo: UserRole | null,
  base: LayoutDashboard,
  generaId: () => string = nuovoId,
): LetturaLayout {
  if (grezzo === null || grezzo === undefined) return { esito: 'nessuno', layout: base, conservati: [] };
  if (!oggetto(grezzo) || !Array.isArray(grezzo.widget)) {
    return { esito: 'illeggibile', layout: base, conservati: [] };
  }
  const versione = grezzo.versione === undefined ? 1 : Number(grezzo.versione);
  if (!Number.isInteger(versione) || versione < 1) return { esito: 'illeggibile', layout: base, conservati: [] };
  if (versione > VERSIONE_LAYOUT) return { esito: 'versione-futura', layout: base, conservati: [] };

  const widget: WidgetIstanza[] = [];
  const conservati: Record<string, unknown>[] = [];
  const idUsati = new Set<string>();
  const copie = new Map<TipoWidget, number>();
  let corretto = false;

  for (const voce of grezzo.widget) {
    if (!oggetto(voce) || typeof voce.tipo !== 'string') {
      corretto = true;
      continue;
    }
    if (!eTipoWidget(voce.tipo) || !ruoloVede(voce.tipo, ruolo)) {
      conservati.push(voce);
      continue;
    }
    const giaPresenti = copie.get(voce.tipo) ?? 0;
    if (giaPresenti >= CATALOGO[voce.tipo].massimo || widget.length >= MAX_WIDGET) {
      corretto = true;
      continue;
    }
    copie.set(voce.tipo, giaPresenti + 1);
    const letto = leggiVoce(voce, idUsati, generaId);
    if (letto.corretto) corretto = true;
    widget.push(letto.widget);
  }

  return {
    esito: corretto ? 'corretto' : 'ok',
    layout: { versione: VERSIONE_LAYOUT, widget },
    conservati,
  };
}

/** Cosa si scrive nel database: i widget visibili piu' quelli conservati. */
export function scriviLayout(
  layout: LayoutDashboard,
  conservati: readonly Record<string, unknown>[],
): { versione: number; widget: unknown[] } {
  return {
    versione: VERSIONE_LAYOUT,
    widget: [
      ...layout.widget.map(({ id, tipo, taglia, impostazioni }) => ({ id, tipo, taglia, impostazioni })),
      ...conservati,
    ],
  };
}

/** Stessi widget, nello stesso ordine, con taglie e impostazioni uguali (gli id non contano). */
export function stessaDisposizione(a: LayoutDashboard, b: LayoutDashboard): boolean {
  const firma = (l: LayoutDashboard) =>
    JSON.stringify(l.widget.map(({ tipo, taglia, impostazioni }) => [tipo, taglia, impostazioni]));
  return firma(a) === firma(b);
}

// ---------------------------------------------------------------------------
// Le modifiche
// ---------------------------------------------------------------------------

export type VersoSpostamento = 'prima' | 'dopo' | 'inizio' | 'fine';

export type AzioneLayout =
  | { tipo: 'aggiungi'; widget: TipoWidget }
  | { tipo: 'rimuovi'; id: string }
  | { tipo: 'taglia'; id: string; taglia: Taglia }
  | { tipo: 'sposta'; id: string; verso: VersoSpostamento }
  | { tipo: 'riordina'; ordine: readonly string[] }
  | { tipo: 'impostazioni'; id: string; impostazioni: unknown }
  | { tipo: 'sostituisci'; layout: LayoutDashboard };

export interface ContestoModifica {
  ruolo: UserRole | null;
  generaId: () => string;
}

/** Si puo' aggiungere un altro widget di questo tipo? */
export function puoAggiungere(layout: LayoutDashboard, tipo: TipoWidget, ruolo: UserRole | null): boolean {
  const copie = layout.widget.filter((w) => w.tipo === tipo).length;
  return ruoloVede(tipo, ruolo) && copie < CATALOGO[tipo].massimo && layout.widget.length < MAX_WIDGET;
}

function spostaId(ids: readonly string[], id: string, verso: VersoSpostamento): string[] {
  const da = ids.indexOf(id);
  if (da < 0) return [...ids];
  const a = verso === 'inizio' ? 0
    : verso === 'fine' ? ids.length - 1
    : verso === 'prima' ? Math.max(0, da - 1)
    : Math.min(ids.length - 1, da + 1);
  const senza = ids.filter((x) => x !== id);
  return [...senza.slice(0, a), id, ...senza.slice(a)];
}

function riordina(layout: LayoutDashboard, ordine: readonly string[]): LayoutDashboard {
  const perId = new Map(layout.widget.map((w) => [w.id, w]));
  const stessiId = ordine.length === layout.widget.length && ordine.every((id) => perId.has(id))
    && new Set(ordine).size === ordine.length;
  if (!stessiId) return layout;
  return { ...layout, widget: ordine.map((id) => perId.get(id) as WidgetIstanza) };
}

function cambiaWidget(
  layout: LayoutDashboard,
  id: string,
  cambia: (w: WidgetIstanza) => WidgetIstanza,
): LayoutDashboard {
  return { ...layout, widget: layout.widget.map((w) => (w.id === id ? cambia(w) : w)) };
}

/** Applica una modifica e restituisce una disposizione nuova. Le modifiche non ammesse non fanno niente. */
export function modificaLayout(
  layout: LayoutDashboard,
  azione: AzioneLayout,
  ctx: ContestoModifica,
): LayoutDashboard {
  switch (azione.tipo) {
    case 'aggiungi': {
      if (!puoAggiungere(layout, azione.widget, ctx.ruolo)) return layout;
      const def = CATALOGO[azione.widget];
      const idUsati = new Set(layout.widget.map((w) => w.id));
      let id = ctx.generaId();
      while (idUsati.has(id)) id = ctx.generaId();
      const nuovo: WidgetIstanza = {
        id,
        tipo: azione.widget,
        taglia: def.tagliaPredefinita,
        impostazioni: def.impostazioniPredefinite,
      };
      return { ...layout, widget: [...layout.widget, nuovo] };
    }
    case 'rimuovi':
      return { ...layout, widget: layout.widget.filter((w) => w.id !== azione.id) };
    case 'taglia':
      return cambiaWidget(layout, azione.id, (w) =>
        CATALOGO[w.tipo].taglie.includes(azione.taglia) ? { ...w, taglia: azione.taglia } : w);
    case 'sposta':
      return riordina(layout, spostaId(layout.widget.map((w) => w.id), azione.id, azione.verso));
    case 'riordina':
      return riordina(layout, azione.ordine);
    case 'impostazioni':
      return cambiaWidget(layout, azione.id, (w) =>
        ({ ...w, impostazioni: CATALOGO[w.tipo].leggiImpostazioni(azione.impostazioni) }));
    case 'sostituisci':
      return azione.layout;
    default:
      return layout;
  }
}
